import React, { useCallback, Fragment, ReactElement } from 'react';
import { Link as LinkInternal } from 'react-router';
import Link from '@mui/material/Link';
import TextBlock from '../text-block/text-block.component';
import RequiredMark from '../required-mark/required-mark.component';
import StructureService from '../../services/structure-service';
import FormatService from '../../services/format-service';
import useCurrentUserPermissions from '../../hooks/use-current-user-permissions';
import useStylesFilteredLink from './filtered-link.styles';

const FilteredLink: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const PREFIX_PHONE: string = 'tel:';
  const PREFIX_EMAIL: string = 'mailto:';
  const PREFIX_PHONE_EXTENSION: string = ';ext=';
  const TARGET_BLANK: string = '_blank';

  const {
    isBlack,
    isPhone,
    isEmail,
    isExternal,
    isSwitcher,
    isDisabled,
    isDownloadLink,
    withNewWindow,
    withRequiredMark,
    link,
    permission,
    permissions,
    clickDeps,
    className,
    activeClassName,
    phoneExtension,
    onClick,
    onMouseDown,
    children,
  } = props;

  const { classes } = useStylesFilteredLink();
  const permissionList: string[] = useCurrentUserPermissions();

  const linkPermissions: string[] = permission ? [permission] : (permissions || []);
  const isLinkAllowed: boolean = linkPermissions && linkPermissions.length
    ? linkPermissions.reduce((isAllowed: boolean, permissionItem: string): boolean => (
      isAllowed || StructureService.checkPermission(permissionItem, permissionList)
    ), false) : true;

  const switcherClassName: string | undefined = isSwitcher ? classes.filteredLinkSwitcher : undefined;
  const downloadClassName: string | undefined = isDownloadLink ? classes.filteredLinkDownload : undefined;
  const blackClassName: string | undefined = isBlack ? classes.filteredLinkBlack : undefined;

  const LinkComponent: ReactComponent = (
    isExternal || isEmail || isPhone || isSwitcher ? Link : LinkInternal
  ) as ReactComponent;

  let formattedLink: string | null = null;
  if (link) {
    formattedLink = link;

    if (isPhone) {
      formattedLink = `${PREFIX_PHONE}${formattedLink}`
        + `${phoneExtension ? `${PREFIX_PHONE_EXTENSION}${phoneExtension}` : ''}`;
    }

    if (isEmail) {
      formattedLink = `${PREFIX_EMAIL}${formattedLink}`;
    }
  }

  const attributes: LinkAttributes = {};
  if (!isSwitcher && formattedLink) {
    if (isExternal || isEmail || isPhone) {
      attributes.href = formattedLink;
    } else {
      attributes.to = formattedLink;
    }

    if (isExternal || withNewWindow) {
      attributes.target = TARGET_BLANK;
    }
  }

  const linkClick: MouseEventHandlerFunction = useCallback((event: React.MouseEvent<Element, MouseEvent>): void => {
    if (onClick) {
      onClick(event);
    }

    event.stopPropagation();
  }, [...(clickDeps || [])]);

  const mouseDown: MouseEventHandlerFunction = useCallback((event: React.MouseEvent<Element, MouseEvent>): void => {
    if (onMouseDown) {
      onMouseDown(event);
    }

    event.stopPropagation();
  }, [...(clickDeps || [])]);

  return (
    isLinkAllowed && !isDisabled && (!!formattedLink || isSwitcher) ? (
      <Fragment>
        <LinkComponent
          className={FormatService.formatClassNames(
            classes.filteredLink, switcherClassName, downloadClassName, blackClassName, activeClassName, className,
          )}
          onClick={linkClick}
          onMouseDown={mouseDown}
          {...attributes}
        >
          {children}
        </LinkComponent>
        {
          withRequiredMark && (
            <RequiredMark />
          )
        }
      </Fragment>
    ) : (
      <TextBlock className={className || undefined}>
        {children}
      </TextBlock>
    )
  );
});

export default FilteredLink;
